<template>
  <b-container fluid class="create-punch-card-container">
    <CreateCampaignMilestone />
    <div class="my-3">
      <component :is="currentComponent"></component>
    </div>
    <div class="create-punch-card-footer py-2 d-flex align-items-center justify-content-center">
      <b-button variant="outline-secondary" class="btn-pill mr-3" v-if="campaignData.active !== 'details'"
        @click="back()" :disabled="loading || uploadingImg || isSaving">Back</b-button>
      <b-button variant="primary" class="btn-pill" @click="proceed()" :disabled="loading || uploadingImg || isSaving">
        {{ currentStep === 3 ? "Create" : "Proceed" }}
        <b-spinner small class="ml-1" v-if="loading || uploadingImg || isSaving"></b-spinner>
      </b-button>
    </div>
  </b-container>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { mapFields } from "vuex-map-fields";
import CreateCampaignMilestone from "@/components/punch-card/CreateCampaignMilestone.vue";
import PunchCardDetails from "@/components/punch-card/create-campaign/Details.vue";
import PunchCardExecution from "@/components/punch-card/create-campaign/Execution.vue";
import PunchCardDesign from "@/components/punch-card/create-campaign/Design.vue";
import { isValidUrl } from "@/components/punch-card/punch.card.utilities";

export default {
  components: { CreateCampaignMilestone, PunchCardDetails, PunchCardExecution, PunchCardDesign },
  computed: {
    ...mapFields("punchCard", ["campaignData", "errors", "modalOpeningKey", "isSaving"]),
    ...mapGetters("punchCard", ["currentStep", "loading", "uploadingImg"]),
    currentComponent: {
      get() {
        return this.routeNames[this.campaignData.active];
      },
      set(component) {
        return component;
      }
    }
  },
  data() {
    return {
      routeNames: {
        details: "PunchCardDetails",
        execution: "PunchCardExecution",
        design: "PunchCardDesign"
      }
    }
  },
  mounted() {
    this.$root.$on("proceedToNextStep", (source) => this.handleProceedNextStep(source));
    this.$root.$on("createCampaign", () => this.handleCreateCampaign());
    this.errors = null;
  },
  beforeDestroy() {
    this.$root.$off("proceedToNextStep");
    this.$root.$off("createCampaign");
    this.$store.commit("punchCard/INIT_CAMPAIGN_DATA");
  },
  methods: {
    ...mapActions({
      "uploadImage": "punchCard/punchCardUploadImage"
    }),
    back() {
      if (this.campaignData.milestones[this.campaignData.active] > 1) {
        const back = this.campaignData.milestones[this.campaignData.active] - 1;
        const backRoute = Object.keys(this.campaignData.milestones).find(key => this.campaignData.milestones[key] === back);

        this.handleChangeRoute(backRoute);
      }
    },
    proceed() {
      this.$root.$emit("validateForm");
    },
    handleProceedNextStep(source) {
      this.modalOpeningKey++;

      if (this.campaignData.milestones[this.campaignData.active] < 3 && source === "create") {
        const next = this.campaignData.milestones[this.campaignData.active] + 1;
        const nextRoute = Object.keys(this.campaignData.milestones).find(key => this.campaignData.milestones[key] === next);

        this.handleChangeRoute(nextRoute);
      }
    },
    handleChangeRoute(route) {
      this.campaignData.active = route;
    },
    async handleCreateCampaign() {
      const {
        active,
        milestones,
        designVisibility,
        ...form
      } = this.campaignData;

      const {
        publishing_time,
        ...execution
      } = {
        ...form.execution,
        date_start: !form.execution.is_open_duration / 1 ? null : `${form.execution.date_start} 00:00:00`,
        date_end: !form.execution.is_open_duration / 1 ? null : `${form.execution.date_end} 23:59:59`,
        publish_date_same_as_start_date: form.execution.publish_date_same_as_start_date / 1,
        publishing_date: `${form.execution.publishing_date} ${form.execution.publishing_time.HH}:${form.execution.publishing_time.mm}:00`,
        is_open_duration: !form.execution.is_open_duration / 1,
        is_open_to_guest: form.execution.is_open_to_guest / 1
      };

      const {
        customize,
        stamp_base64,
        ...design
      } = {
        ...form.design,
        ...form.design.customize,
        stamp_icon: isValidUrl(form.design.stamp_icon) ? form.design.stamp_icon : form.design.stamp_base64,
        number_of_stamps: Number(form.design.number_of_stamps)
      };

      const payload = {
        ...form.details,
        name: form.details.name.trim(),
        ...execution,
        ...design
      };

      payload.milestones = payload.milestones.map((milestone) => {
        const { is_main, reward, ...item } = {
          ...milestone,
          reward_name: milestone.reward.name,
          reward_uuid: milestone.reward.uuid,
          stamps_count: milestone.is_main ? Number(payload.number_of_stamps) : milestone.stamps_count
        }
        return item;
      });

      const imagesToUpload = {};
      const imagesToUploadArray = ["campaign_background_image", "stamp_icon", "reward_image", "image"];

      imagesToUploadArray.forEach((imgKey) => {
        if (imgKey === "reward_image") imagesToUpload[imgKey] = isValidUrl(payload.milestones[0].reward_image);
        else if (payload[imgKey]) imagesToUpload[imgKey] = isValidUrl(payload[imgKey]);
      });

      const imageUploadPromises = imagesToUploadArray.map(async (imgKey, imgIndex) => {
        this.isSaving = true;
        if (imgKey === "reward_image" && !imagesToUpload[imgKey]) {
          await this.uploadImage({
            file: payload.milestones[0].reward_image.split(";base64,").pop(),
            type: 3
          }).then((url) => payload.milestones[0].reward_image = url);
        } else if (imgKey === "stamp_icon" && !imagesToUpload[imgKey]) {
          await this.uploadImage({
            file: payload.stamp_icon.split(";base64,").pop(),
            type: 2,
            icon: form.design.stamp_preset
          }).then((url) => payload.stamp_icon = url);
        } else if (imagesToUpload[imgKey] !== undefined && !imagesToUpload[imgKey]) {
          await this.uploadImage({
            file: payload[imgKey].split(";base64,").pop(),
            type: imgIndex + 1
          }).then((url) => payload[imgKey] = url);
        }
      });

      Promise.all(imageUploadPromises).then(() => {
        if (!this.errors) this.submitCampaign(payload);
      });
    },
    async submitCampaign(payload) {
      await this.$store.dispatch("punchCard/createPunchCardCampaign", payload);

      if (!this.errors) this.$router.push({ name: "PunchCard" });

      else {
        const detailsErrors = ["name", "description", "terms_condition"];
        const executionErrors = ["date_start", "date_end", "publishing_date", "target_audience", "frequency"];
        const designErrors = ["number_of_stamps", "stamp_icon", "milestones", "image"];
        const errorKeys = Object.keys(this.errors);

        this.$nextTick(() => {
          if (detailsErrors.some((key) => errorKeys.includes(key))) this.campaignData.active = "details";
          else if (executionErrors.some((key) => errorKeys.includes(key))) this.campaignData.active = "execution";
          else if (designErrors.some((key) => errorKeys.includes(key))) this.campaignData.active = "design";
        });
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.create-punch-card-container {
  max-width: 720px;

  .create-punch-card-footer {
    &>button {
      width: 183px;
      height: 44px;
    }
  }
}
</style>

<style lang="scss">
@import "~@/assets/scss/punch-card/_form.scss";
</style>
